// Tài khoản đã đăng nhập trên máy này — màn Đăng nhập hiện thành danh sách "Đăng nhập nhanh trên máy này".
// Chỉ lưu thứ cần để hiện danh sách và điền sẵn ô tài khoản: chuỗi đăng nhập, tên đơn vị, loại tài khoản.
// KHÔNG BAO GIỜ lưu mật khẩu hay token. Người dùng xoá được từng tài khoản khỏi máy.

import { normalizePhoneVN } from './format'

export type AccountKind = 'shop' | 'ctv' | 'agency' | 'npp' | 'staff' | 'admin'

export interface RememberedAccount {
  /** Khoá so trùng: email chữ thường / số điện thoại chuẩn hoá */
  id: string
  /** Chuỗi người dùng gõ khi đăng nhập (số điện thoại hoặc email) — đăng nhập nhanh gửi lại y nguyên */
  identifier: string
  /** Tên hiện trên danh sách: tên đơn vị đang mở */
  name: string
  kind: AccountKind
  lastUsed: number
}

export const KIND_LABEL: Record<AccountKind, string> = {
  shop: 'Shop',
  ctv: 'Cộng tác viên',
  agency: 'Đại lý',
  npp: 'Nhà phân phối',
  staff: 'Nhân viên',
  admin: 'Quản trị',
}

export const MAX_REMEMBERED = 5
const KEY = 'st-remembered-accounts'
/** Đăng nhập cần mã email: nhớ tạm chuỗi đăng nhập tới khi nhập đúng mã (màn /2fa) */
const PENDING_KEY = 'st-login-pending'

export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

// Chế độ ẩn danh / chặn dữ liệu trang: truy cập storage có thể ném lỗi — khi đó coi như không có gì để nhớ
function localStore(): StorageLike | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}
function sessionStore(): StorageLike | null {
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}
function safeGet(s: StorageLike | null, key: string): string | null {
  try {
    return s?.getItem(key) ?? null
  } catch {
    return null
  }
}
function safeSet(s: StorageLike | null, key: string, value: string) {
  try {
    s?.setItem(key, value)
  } catch {
    // đầy bộ nhớ / bị chặn — lần sau đăng nhập lại nhớ
  }
}
function safeRemove(s: StorageLike | null, key: string) {
  try {
    s?.removeItem(key)
  } catch {
    // như trên
  }
}

export function accountId(identifier: string): string {
  const s = identifier.trim()
  if (s.includes('@')) return s.toLowerCase()
  return normalizePhoneVN(s) || s.replace(/\D/g, '') || s.toLowerCase()
}

/** "0912345678" → "0912 ••• 678"; "nguyen@pthub.vn" → "ng•••@pthub.vn" */
export function maskIdentifier(identifier: string): string {
  const s = identifier.trim()
  const at = s.indexOf('@')
  if (at > 0) return `${s.slice(0, Math.min(2, at))}•••${s.slice(at)}`
  const d = s.replace(/\D/g, '')
  if (d.length < 7) return s
  return `${d.slice(0, 4)} ••• ${d.slice(-3)}`
}

/** Chữ cái đầu của hai từ cuối: "Shop Hoa Mai" → "HM", "Lan Anh" → "LA" */
export function initialsOf(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  const letters = (words.length >= 2 ? words.slice(-2) : words).map((w) => w.charAt(0)).join('')
  return letters.toLocaleUpperCase('vi') || '?'
}

interface SessionShop {
  shopId: string
  name: string
  role: string
  tenantType?: string
}

export function kindOf(shop: SessionShop | undefined, nppAdmin: boolean): AccountKind {
  if (nppAdmin) return 'npp'
  if (!shop) return 'shop'
  if (shop.role === 'staff') return 'staff'
  const t = shop.tenantType || shop.role
  if (t === 'ctv') return 'ctv'
  if (t === 'agency') return 'agency'
  if (t === 'admin' || t === 'super_admin') return 'admin'
  return 'shop'
}

/** Tài khoản vừa đăng nhập xong → mục để nhớ (tên = đơn vị đang mở; chưa có đơn vị thì số / email đã che). */
export function accountFromSession(
  s: { identifier: string; shops: SessionShop[]; activeShopId: string | null; nppAdmin: boolean },
  now = Date.now(),
): RememberedAccount {
  const shop = s.shops.find((x) => x.shopId === s.activeShopId) ?? s.shops[0]
  const name = shop?.name?.trim() || maskIdentifier(s.identifier)
  return { id: accountId(s.identifier), identifier: s.identifier.trim(), name, kind: kindOf(shop, s.nppAdmin), lastUsed: now }
}

const KINDS = new Set<string>(Object.keys(KIND_LABEL))
function isAccount(x: unknown): x is RememberedAccount {
  if (!x || typeof x !== 'object') return false
  const a = x as Record<string, unknown>
  return (
    typeof a.id === 'string' && a.id !== '' &&
    typeof a.identifier === 'string' && a.identifier !== '' &&
    typeof a.name === 'string' &&
    typeof a.kind === 'string' && KINDS.has(a.kind) &&
    typeof a.lastUsed === 'number' && Number.isFinite(a.lastUsed)
  )
}

const byRecent = (a: RememberedAccount, b: RememberedAccount) => b.lastUsed - a.lastUsed

/** Chuỗi trong storage → danh sách hợp lệ, mới dùng xếp trước (dữ liệu hỏng / sửa tay thì bỏ qua). */
export function parseAccounts(raw: string | null): RememberedAccount[] {
  if (!raw) return []
  try {
    const v: unknown = JSON.parse(raw)
    if (!Array.isArray(v)) return []
    const seen = new Set<string>()
    return v
      .filter(isAccount)
      .sort(byRecent)
      .filter((a) => !seen.has(a.id) && seen.add(a.id))
      .slice(0, MAX_REMEMBERED)
  } catch {
    return []
  }
}

export function upsertAccount(list: RememberedAccount[], entry: RememberedAccount): RememberedAccount[] {
  return [entry, ...list.filter((a) => a.id !== entry.id)].sort(byRecent).slice(0, MAX_REMEMBERED)
}

export function readRemembered(store: StorageLike | null = localStore()): RememberedAccount[] {
  return parseAccounts(safeGet(store, KEY))
}

export function rememberAccount(entry: RememberedAccount, store: StorageLike | null = localStore()): void {
  safeSet(store, KEY, JSON.stringify(upsertAccount(readRemembered(store), entry)))
}

export function forgetAccount(id: string, store: StorageLike | null = localStore()): RememberedAccount[] {
  const next = readRemembered(store).filter((a) => a.id !== id)
  if (next.length) safeSet(store, KEY, JSON.stringify(next))
  else safeRemove(store, KEY)
  return next
}

export function setPendingLogin(identifier: string, store: StorageLike | null = sessionStore()): void {
  safeSet(store, PENDING_KEY, identifier.trim())
}

/** Lấy (và xoá) chuỗi đăng nhập đang chờ mã email. */
export function takePendingLogin(store: StorageLike | null = sessionStore()): string | null {
  const v = safeGet(store, PENDING_KEY)
  safeRemove(store, PENDING_KEY)
  return v || null
}
